import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, CalendarCheck } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import BookingLink, { BookingText } from '@/components/BookingLink';

// Official company number (Edmonton).
const PHONE_DISPLAY = '780-394-2451';
const PHONE_TEL = '+17803942451';

// Private/utility surfaces where a marketing call-bar is inappropriate.
const HIDDEN_PREFIXES = ['/auth', '/admin', '/dashboard'];

/**
 * Mobile-only sticky bottom action bar — pinned to the bottom of the
 * viewport on every page (including the homepage) at all scroll positions.
 * Two actions: Call Now (tel: the official number) and Book a Free Consultation (Cal.com).
 * Hidden on lg+ where the desktop floating CTA + header button take over.
 */
const MobileStickyCTA = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const path = location.pathname.replace(/^\/fr/, '') || '/';
  const hidden = HIDDEN_PREFIXES.some((p) => path.startsWith(p));

  // Reserve space at the bottom of the page so the bar never covers the
  // footer / last bit of content on mobile. Toggled only while the bar shows.
  useEffect(() => {
    const body = document.body;
    if (hidden) {
      body.classList.remove('has-mobile-cta');
      return;
    }
    body.classList.add('has-mobile-cta');
    return () => body.classList.remove('has-mobile-cta');
  }, [hidden]);

  if (hidden) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 flex items-stretch gap-2 border-t border-border bg-background/95 px-3 py-2.5 backdrop-blur lg:hidden"
      style={{ paddingBottom: 'calc(0.625rem + env(safe-area-inset-bottom))' }}
      role="region"
      aria-label={t('mobileCta.region')}
    >
      <a
        href={`tel:${PHONE_TEL}`}
        onClick={() =>
          trackEvent('cta_click', { cta_label: 'Call Now', cta_location: 'mobile_sticky_bar' })
        }
        className="inline-flex flex-none items-center justify-center gap-2 rounded-full border border-primary px-4 py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label={t('mobileCta.callAria', { phone: PHONE_DISPLAY })}
      >
        <Phone className="h-4 w-4" aria-hidden="true" />
        {t('mobileCta.call')}
      </a>
      <BookingLink
        location="mobile_sticky_bar"
        onClick={() =>
          trackEvent('cta_click', { cta_label: 'Book a Free Consultation', cta_location: 'mobile_sticky_bar' })
        }
        className="inline-flex min-w-0 flex-1 items-center justify-center gap-2 rounded-full bg-primary px-3 py-3 text-center text-sm font-semibold leading-tight text-primary-foreground shadow-[0_8px_24px_-8px_hsl(var(--primary)/0.6)] transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <CalendarCheck className="hidden h-4 w-4 shrink-0 sm:inline" aria-hidden="true" />
        <BookingText />
      </BookingLink>
    </div>
  );
};

export default MobileStickyCTA;
